"use client"

import type { FinalExecutionOutcome } from "@near-wallet-selector/core"
import {
  useConnection as useSolanaConnection,
  useWallet as useSolanaWallet,
} from "@solana/wallet-adapter-react"
import { useWalletModal } from "@solana/wallet-adapter-react-ui"
import { clearActiveSession } from "@src/actions/auth"
import { BaseError } from "@src/components/DefuseSDK/errors/base"
import type {
  SendTransactionStellarParams,
  SendTransactionTronParams,
} from "@src/components/DefuseSDK/types/deposit"
import { extractWalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import { UNIFIED_FEED_QUERY_KEY } from "@src/features/activity/lib/useUnifiedFeed"
import {
  useWebAuthnActions,
  useWebAuthnCurrentCredential,
  useWebAuthnUIStore,
} from "@src/features/webauthn/hooks/useWebAuthnStore"
import { teardownPrivateMode } from "@src/hooks/teardownPrivateMode"
import { useSignInLogger } from "@src/hooks/useSignInLogger"
import { useWalletAuth } from "@src/hooks/useWalletAuth"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import {
  signTransactionStellar,
  submitTransactionStellar,
  useStellarWallet,
} from "@src/providers/StellarWalletProvider"
import { useTronWallet } from "@src/providers/TronWalletProvider"
import { useDisconnectingStore } from "@src/stores/useDisconnectingStore"
import {
  type LastConnectedWallet,
  useLastConnectedWalletStore,
} from "@src/stores/useLastConnectedWalletStore"
import type {
  SendTransactionEVMParams,
  SendTransactionSolanaParams,
  SendTransactionTonParams,
  SignAndSendTransactionsParams,
} from "@src/types/interfaces"
import { WalletConnectionFailure } from "@src/utils/classifyWalletConnectionFailure"
import { logger } from "@src/utils/logger"
import { parseTonAddress } from "@src/utils/parseTonAddress"
import { trackEvent } from "@src/utils/trackEvent"
import { useQueryClient } from "@tanstack/react-query"
import { Cell } from "@ton/ton"
import {
  useIsConnectionRestored as useIsTonConnectionRestored,
  useTonConnectModal,
  useTonConnectUI,
  useTonWallet,
} from "@tonconnect/ui-react"
import type { SendTransactionResponse } from "@tonconnect/ui-react"
import type { SendTransactionParameters } from "@wagmi/core"
import { useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import {
  type Connector,
  useAccount,
  useConnect,
  useConnections,
  useDisconnect,
} from "wagmi"
import { disconnectWagmiConnections } from "./disconnectWagmiConnections"
import { useEVMWalletActions } from "./useEVMWalletActions"

export enum ChainType {
  Near = "near",
  EVM = "evm",
  Solana = "solana",
  WebAuthn = "webauthn",
  Ton = "ton",
  Stellar = "stellar",
  Tron = "tron",
}

export type State = {
  chainType?: ChainType
  network?: string
  address?: string
  displayAddress?: string
  walletId?: string
  isAuthorized: boolean
  isAuthValidating: boolean // true while server-side token validation is in progress
  isSessionExpired: boolean // true when token existed but validation failed
  isFake: boolean // in most cases, this is used for testing purposes only
}

interface ConnectWalletAction {
  signIn: (params: {
    id: ChainType
    connector?: Connector
    walletId?: string
    adapterName?: string
  }) => Promise<boolean>
  signOut: (params: { id: ChainType }) => Promise<void>
  sendTransaction: (params: {
    id: ChainType
    tx?:
      | SignAndSendTransactionsParams["transactions"]
      | SendTransactionEVMParams["transactions"]
      | SendTransactionSolanaParams["transactions"]
      | SendTransactionTonParams["transactions"]
      | SendTransactionStellarParams
      | SendTransactionTronParams
  }) => Promise<string | FinalExecutionOutcome[] | SendTransactionResponse>
  connectors: Connector[]
  state: State
  /** True when any wallet adapter is in a connecting state */
  isLoading: boolean
  /** True when EVM wallet is in a reconnecting state */
  isReconnecting: boolean
  /** Error message from the last failed signIn attempt, null if none */
  signInError: string | null
  /** Clear the current signIn error */
  clearSignInError: () => void
}

const defaultState: State = {
  chainType: undefined,
  network: undefined,
  address: undefined,
  displayAddress: undefined,
  isAuthorized: false,
  isAuthValidating: false,
  isSessionExpired: false,
  isFake: false,
}

function getLastConnectedWalletFromSignIn(params: {
  id: ChainType
  connector?: Connector
  walletId?: string
  adapterName?: string
}): LastConnectedWallet | null {
  switch (params.id) {
    case ChainType.Near:
      return params.walletId
        ? { chain: "near", walletId: params.walletId }
        : null
    case ChainType.EVM:
      return params.connector
        ? { chain: "evm", walletId: params.connector.id }
        : null
    case ChainType.Solana:
      return params.adapterName
        ? { chain: "solana", walletId: params.adapterName }
        : null
    case ChainType.Stellar:
      return params.walletId
        ? { chain: "stellar", walletId: params.walletId }
        : null
    case ChainType.Ton:
    case ChainType.Tron:
    case ChainType.WebAuthn:
      return null
  }
}

export const useConnectWallet = (): ConnectWalletAction => {
  let state: State = defaultState
  const queryClient = useQueryClient()

  /**
   * NEAR:
   * Down below are Near Wallet handlers and actions
   */
  const nearWallet = useNearWallet()

  if (nearWallet.accountId != null) {
    state = {
      address: nearWallet.accountId,
      displayAddress: nearWallet.accountId,
      network: "near:mainnet",
      chainType: ChainType.Near,
      walletId: nearWallet.connectedWalletId ?? undefined,
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * EVM:
   * Down below are Wagmi Wallet handlers and actions
   */
  const evmWalletConnect = useConnect()
  const evmWalletDisconnect = useDisconnect()
  const evmWalletAccount = useAccount()
  const evmWalletConnections = useConnections()
  const { sendTransactions } = useEVMWalletActions()

  const handleSignInViaWagmi = async ({
    connector,
  }: {
    connector: Connector
  }): Promise<void> => {
    if (!connector) {
      throw new Error("Invalid connector")
    }
    await evmWalletConnect.connectAsync({ connector })
  }
  const handleSignOutViaWagmi = async () => {
    await disconnectWagmiConnections({
      connections: evmWalletConnections,
      accountAddress: evmWalletAccount.address,
      disconnectClient: evmWalletDisconnect,
    })
  }

  // We check `account.chainId` instead of `account.chain` to determine if
  // the user is connected. This is because the user might be connected to
  // an unsupported chain (so `.chain` will undefined), but we still want
  // to recognize that their wallet is connected.
  if (evmWalletAccount.address != null && evmWalletAccount.chainId) {
    state = {
      address: evmWalletAccount.address,
      displayAddress: evmWalletAccount.address,
      network: evmWalletAccount.chainId
        ? `eth:${evmWalletAccount.chainId}`
        : "unknown",
      chainType: ChainType.EVM,
      walletId: evmWalletAccount.connector?.id,
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * Solana:
   * Down below are Solana Wallet handlers and actions
   *
   * Ensure Solana Wallet state overrides EVM Wallet state:
   * Context:
   *   Phantom Wallet supports both Solana and EVM chains.
   * Issue:
   *   When Phantom Wallet connects, it may emit an EVM connection event.
   *   This causes `wagmi` to connect to the EVM chain, leading to unexpected
   *   address switching. Placing Solana Wallet state last prevents this.
   */
  const { setVisible } = useWalletModal()
  const solanaWallet = useSolanaWallet()
  const solanaConnection = useSolanaConnection()
  const handleSignInViaSolanaSelector = async (adapterName?: string) => {
    if (adapterName) {
      const adapter = solanaWallet.wallets.find(
        (w) => w.adapter.name === adapterName
      )
      if (!adapter) {
        throw new Error(`Solana adapter not found: ${adapterName}`)
      }
      // Persist the selection so future page loads can autoConnect to it.
      solanaWallet.select(adapter.adapter.name)
      // SolanaWalletProvider gates autoConnect to Installed adapters only —
      // that prevents page-load redirects from Loadable wallets like
      // Solflare's web SDK. Explicit user clicks on Loadable adapters
      // (Hot via WalletConnect, mobile-only adapters) need a direct
      // connect() call so they actually proceed instead of silently selecting.
      await adapter.adapter.connect()
      return
    }
    setVisible(true)
  }

  const handleSignOutViaSolanaSelector = async () => {
    await solanaWallet.disconnect()
    // Issue: Phantom wallet also connects EVM wallet when it connects Solana wallet
    await handleSignOutViaWagmi()
  }

  if (solanaWallet.publicKey != null) {
    state = {
      address: solanaWallet.publicKey.toBase58(),
      displayAddress: solanaWallet.publicKey.toBase58(),
      network: "sol:mainnet",
      chainType: ChainType.Solana,
      walletId: solanaWallet.wallet?.adapter.name,
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * WebAuthn:
   * Down below are WebAuthn Wallet handlers and actions
   */
  const currentPasskey = useWebAuthnCurrentCredential()
  const webAuthnActions = useWebAuthnActions()
  const webAuthnUI = useWebAuthnUIStore()

  if (currentPasskey != null) {
    state = {
      address: currentPasskey.publicKey,
      displayAddress: currentPasskey.publicKey,
      chainType: ChainType.WebAuthn,
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * TON:
   * Down below are TON Wallet handlers and actions
   */
  const tonWallet = useTonWallet()
  const tonConnectModal = useTonConnectModal()
  const [tonConnectUI] = useTonConnectUI()
  const isTonConnectionRestored = useIsTonConnectionRestored()

  if (tonWallet) {
    state = {
      address: tonWallet.account.publicKey,
      displayAddress: parseTonAddress(tonWallet.account.address),
      network: "ton",
      chainType: ChainType.Ton,
      walletId: "ton",
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * Stellar:
   * Down below are Stellar Wallet handlers and actions
   */
  const {
    publicKey,
    connectedWalletId: stellarConnectedWalletId,
    connect,
    disconnect,
    isLoading: isStellarLoading,
  } = useStellarWallet()

  const handleSignInViaStellar = async (walletId?: string): Promise<void> => {
    await connect(walletId)
  }

  const handleSignOutViaStellar = async (): Promise<void> => {
    await disconnect()
  }

  if (publicKey) {
    state = {
      address: publicKey,
      displayAddress: publicKey,
      network: "stellar:mainnet",
      chainType: ChainType.Stellar,
      walletId: stellarConnectedWalletId ?? undefined,
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  /**
   * Tron:
   * Down below are Tron Wallet handlers and actions
   */
  const tronWallet = useTronWallet()

  const handleSignInViaTron = async (): Promise<void> => {
    await tronWallet.connect()
  }

  const handleSignOutViaTron = async (): Promise<void> => {
    await tronWallet.disconnect()
  }

  if (tronWallet.publicKey) {
    state = {
      address: tronWallet.publicKey,
      displayAddress: tronWallet.publicKey,
      network: "tron:mainnet",
      chainType: ChainType.Tron,
      walletId: "tron",
      isAuthorized: false,
      isAuthValidating: false,
      isSessionExpired: false,
      isFake: false,
    }
  }

  const { isAuthorized, isSessionExpired, isValidating } = useWalletAuth(
    state.address,
    state.chainType
  )

  state.isAuthorized = isAuthorized
  state.isAuthValidating = isValidating
  state.isSessionExpired = isSessionExpired

  const impersonatedUser = useImpersonatedUser()
  if (impersonatedUser) {
    state = impersonatedUser
  }

  const { onSignOut } = useSignInLogger(
    state.address,
    state.chainType,
    state.isAuthorized
  )

  const isLoading =
    evmWalletAccount.isConnecting ||
    evmWalletAccount.isReconnecting ||
    solanaWallet.connecting ||
    isStellarLoading ||
    tronWallet.isLoading ||
    nearWallet.isInitializing ||
    !isTonConnectionRestored ||
    webAuthnUI.isSigningIn ||
    webAuthnUI.isCreating

  const { setIsDisconnecting } = useDisconnectingStore()
  const recordLastConnectedWallet = useLastConnectedWalletStore(
    (s) => s.recordLastConnectedWallet
  )

  // TON / Tron post-hoc recording. Their connect calls can resolve before the
  // wallet is actually connected, so wait for provider state to confirm the
  // handshake instead of recording at click time.
  useEffect(() => {
    if (tonWallet != null) {
      recordLastConnectedWallet({ chain: "ton", walletId: "ton" })
    }
  }, [tonWallet, recordLastConnectedWallet])
  useEffect(() => {
    if (tronWallet.publicKey != null) {
      recordLastConnectedWallet({ chain: "tron", walletId: "tron" })
    }
  }, [tronWallet.publicKey, recordLastConnectedWallet])

  const [signInError, setSignInError] = useState<string | null>(null)
  const clearSignInError = useCallback(() => setSignInError(null), [])

  return {
    async signIn(params: {
      id: ChainType
      connector?: Connector
      walletId?: string
      adapterName?: string
    }): Promise<boolean> {
      setSignInError(null)

      // WebAuthn has its own error handling via useWebAuthnUiStore
      if (params.id !== ChainType.WebAuthn) {
        trackEvent("wallet_connect_started", { chain: params.id })
      }

      const strategies = {
        [ChainType.Near]: () => nearWallet.connect(params.walletId),
        [ChainType.EVM]: () =>
          params.connector
            ? handleSignInViaWagmi({ connector: params.connector })
            : undefined,
        [ChainType.Solana]: () =>
          handleSignInViaSolanaSelector(params.adapterName),
        [ChainType.WebAuthn]: () => webAuthnUI.open(),
        [ChainType.Ton]: () => tonConnectModal.open(),
        [ChainType.Stellar]: () => handleSignInViaStellar(params.walletId),
        [ChainType.Tron]: () => handleSignInViaTron(),
      }

      try {
        await strategies[params.id]()
        const lastConnectedWallet = getLastConnectedWalletFromSignIn(params)
        if (lastConnectedWallet) {
          recordLastConnectedWallet(lastConnectedWallet)
        }
        return true
      } catch (error) {
        // WebAuthn has its own error UI via useWebAuthnUiStore
        if (params.id === ChainType.WebAuthn) {
          return false
        }

        const code = extractWalletErrorCode(error, null)

        if (code === "ERR_WALLET_CANCEL_ACTION") {
          return false
        }

        const message =
          code === "ERR_WALLET_POPUP_BLOCKED"
            ? "Popup was blocked by your browser. Please allow popups and try again."
            : "Wallet connection failed. Please try again."

        setSignInError(message)

        const failure = new WalletConnectionFailure(error, code)
        trackEvent("wallet_connect_failed", {
          chain: params.id,
          error_code: code ?? "unknown",
          failure_type: failure.failureType,
          error_message: failure.truncatedMessage,
        })

        if (failure.isActionable) {
          logger.error("Wallet signIn failed", {
            error: failure,
            chain: params.id,
          })
        }
        return false
      }
    },

    async signOut(params: { id: ChainType }): Promise<void> {
      setIsDisconnecting(true)
      // Prevent the @hot-labs/near-connect dark overlay from flashing
      // during Meteor wallet disconnect (see global.css).
      document.documentElement.dataset.disconnecting = ""
      try {
        const strategies = {
          [ChainType.Near]: () => nearWallet.disconnect(),
          [ChainType.EVM]: () => handleSignOutViaWagmi(),
          [ChainType.Solana]: () => handleSignOutViaSolanaSelector(),
          [ChainType.WebAuthn]: () => webAuthnActions.signOut(),
          [ChainType.Ton]: () => tonConnectUI.disconnect(),
          [ChainType.Stellar]: () => handleSignOutViaStellar(),
          [ChainType.Tron]: () => handleSignOutViaTron(),
        }

        // Clear active session cookie first — prevents proxy redirect (/ → /home)
        await clearActiveSession()

        teardownPrivateMode()
        onSignOut()

        // Disconnect wallet first so address becomes null before any query state
        // changes. This prevents WalletVerificationDialog from flashing
        // (it triggers on address != null && !isAuthorized).
        await strategies[params.id]()

        // Evict cached token_validation so reconnecting the same wallet
        // triggers a fresh fetch (which finds the surviving per-wallet
        // refresh token and silently re-authenticates).
        queryClient.removeQueries({ queryKey: ["token_validation"] })
        queryClient.removeQueries({ queryKey: [UNIFIED_FEED_QUERY_KEY] })
        void queryClient.invalidateQueries({
          queryKey: ["swap_history"],
        })
      } catch (error) {
        const originalError = error instanceof Error ? error : undefined
        const errorCode = extractWalletErrorCode(error, null)
        const failure = new WalletConnectionFailure(error, errorCode)
        trackEvent("wallet_disconnect_failed", {
          chain: params.id,
          failure_type: failure.failureType,
          error_message: failure.truncatedMessage,
        })
        if (failure.isActionable) {
          logger.error("Wallet signOut failed", {
            error: failure,
            chain: params.id,
          })
        }
        throw new WalletDisconnectError(
          params.id,
          originalError?.message ?? String(error),
          originalError
        )
      } finally {
        delete document.documentElement.dataset.disconnecting
        setIsDisconnecting(false)
      }
    },

    sendTransaction: async (
      params
    ): Promise<string | FinalExecutionOutcome[] | SendTransactionResponse> => {
      const strategies = {
        [ChainType.Near]: async () => {
          if (!nearWallet.accountId) {
            throw new Error("NEAR wallet not connected")
          }
          return await nearWallet.signAndSendTransactions({
            transactions:
              params.tx as SignAndSendTransactionsParams["transactions"],
          })
        },

        [ChainType.EVM]: async () =>
          await sendTransactions(params.tx as SendTransactionParameters),

        [ChainType.Solana]: async () => {
          const transaction =
            params.tx as SendTransactionSolanaParams["transactions"]
          return await solanaWallet.sendTransaction(
            transaction,
            solanaConnection.connection
          )
        },

        [ChainType.WebAuthn]: async () => {
          throw new Error("WebAuthn does not support transactions")
        },

        [ChainType.Ton]: async () => {
          if (!tonWallet) {
            throw new Error("TON wallet not connected")
          }
          const transaction =
            params.tx as SendTransactionTonParams["transactions"]

          const response = await tonConnectUI.sendTransaction(transaction)
          const cell = Cell.fromBoc(Buffer.from(response.boc, "base64"))[0]
          const hash = cell.hash().toString("hex")
          return hash
        },

        [ChainType.Stellar]: async () => {
          if (!publicKey) {
            throw new Error("Stellar wallet not connected")
          }
          const stellarParams = params.tx as SendTransactionStellarParams
          const xdrString = stellarParams.transaction.toXDR()
          const { signedTxXdr } = await signTransactionStellar(xdrString)
          const txHash = await submitTransactionStellar(signedTxXdr)
          return txHash
        },

        [ChainType.Tron]: async () => {
          if (!tronWallet) {
            throw new Error("Tron wallet not connected")
          }
          const tronParams = params.tx as SendTransactionTronParams
          const txHash = await tronWallet.sendTransaction({
            transaction: tronParams,
          })
          return txHash
        },
      }

      const result = await strategies[params.id]()
      if (result === undefined) {
        throw new Error(`Transaction failed for ${params.id}`)
      }
      return result
    },

    connectors: evmWalletConnect.connectors as Connector[],
    state,
    isLoading,
    isReconnecting: evmWalletAccount.isReconnecting,
    signInError,
    clearSignInError,
  }
}

/**
 * Allows getting the impersonation user from the URL.
 * This is used for testing purposes only.
 * example: "/home?user=evm:0xdeadbeef" will return { method: "evm", identifier: "0xdeadbeef" }
 */
function useImpersonatedUser() {
  const searchParams = useSearchParams()
  const user = searchParams.get("user")
  if (user == null) return null
  const index = user.indexOf(":")
  return {
    chainType: user.slice(0, index) as ChainType,
    address: user.slice(index + 1),
    isAuthorized: true,
    isAuthValidating: false,
    isSessionExpired: false,
    isFake: true,
  }
}

class WalletDisconnectError extends BaseError {
  chainType: ChainType
  originalError?: Error

  constructor(chainType: ChainType, message: string, originalError?: Error) {
    super("Wallet sign out error", {
      cause: originalError,
      metaMessages: [`Chain type: ${chainType}`, `Message: ${message}`],
      name: "WalletDisconnectError",
    })
    this.chainType = chainType
    this.originalError = originalError
  }
}
